import { Collapsible, CollapsibleContent } from "@gigpro/ui/Collapsible";
import { Form } from "@gigpro/ui/Form";
import { Textfield } from "@gigpro/ui/Form/Textfield";
import { useZodForm } from "@gigpro/ui/Form/utils";
import { PaymentElement } from "@stripe/react-stripe-js";
import { useAuth, useIsAdmin } from "domains/auth";
import { JobsTrialStep } from "domains/hire/components/JobsTrialStep";
import {
  type PaymentSourceId,
  useSaveStripePaymentMethod,
} from "domains/locations/containers/StripePaymentMethodUpdate/useSaveStripePaymentMethod";
import React from "react";
import * as z from "zod/mini";

const FormValues = z.object({ email: z.optional(z.email()) });
type FormValues = z.infer<typeof FormValues>;

type JobsTrialPaymentStepProps = Omit<
  React.ComponentProps<typeof JobsTrialStep>,
  "paymentForm" | "startDisabled" | "billedToOrganization" | "onStart"
> & {
  sourceId: PaymentSourceId;
  /** The method is already saved, so a retry only starts the trial. */
  paymentSaved: boolean;
  onSaved: () => void;
  onStart: () => Promise<void>;
};

/** The trial step with Stripe's PaymentElement inline: saves the method, then starts the trial. */
export const JobsTrialPaymentStep = ({
  sourceId,
  paymentSaved,
  onSaved,
  onStart,
  isStarting,
  ...stepProps
}: JobsTrialPaymentStepProps) => {
  const { user } = useAuth();
  const isStaff = useIsAdmin();
  const { isReady, isConfirmed, save } = useSaveStripePaymentMethod(sourceId);
  const [isElementReady, setIsElementReady] = React.useState(false);
  const [paymentType, setPaymentType] = React.useState<string>();
  const [isSaving, setIsSaving] = React.useState(false);
  const [needsVerification, setNeedsVerification] = React.useState(false);
  const [stripeError, setStripeError] = React.useState<string>();
  const isCard = paymentType === "card";

  const form = useZodForm({
    schema: FormValues,
    schemaTransform: (schema) => (isCard ? z.required(schema) : schema),
    // Staff are acting for the business, so their own address must not get its receipts.
    defaultValues: { email: isStaff ? undefined : (user?.email ?? undefined) },
  });

  const saveMethod = async (email: string | undefined) => {
    try {
      const { error, setupIntent } = await save({ email, returnUrl: globalThis.location.href });
      if (error) {
        if (error.type !== "validation_error") setStripeError(error.message);
        return false;
      }
      // Microdeposit bank verification: nothing chargeable until the business confirms the deposits.
      if (setupIntent.status === "requires_action") {
        setNeedsVerification(true);
        return false;
      }
      onSaved();
      return true;
    } catch {
      // Only the PATCH rejects here (confirm failures come back as `error`), and usePaymentSourceUpdate toasts it.
      return false;
    }
  };

  const submit = async ({ email }: FormValues) => {
    setStripeError(undefined);
    setIsSaving(true);
    if (await saveMethod(email)) await onStart();
    setIsSaving(false);
  };

  // Once saved the form is gone (and its email unregistered), so a retry must not re-validate it.
  const handleStart = paymentSaved ? onStart : () => form.handleSubmit(submit)();

  let paymentForm: React.ReactNode;
  if (needsVerification) {
    paymentForm = (
      <p className="font-semibold">
        Stripe will send two small deposits to your bank account in 1-2 business days. Once you've verified them, come
        back to start your free trial.
      </p>
    );
  } else if (paymentSaved) {
    paymentForm = <p className="font-semibold">Payment method saved.</p>;
  } else {
    paymentForm = (
      <Form form={form} onSubmit={submit}>
        <Collapsible open={isElementReady && isCard}>
          <CollapsibleContent>
            <Textfield
              type="email"
              control={form.control}
              name="email"
              shouldUnregister
              label="Receipt email"
              placeholder="jane@gmail.com"
              required
              className="mb-3"
            />
          </CollapsibleContent>
        </Collapsible>
        <PaymentElement
          options={{ readOnly: isConfirmed }}
          onReady={() => setIsElementReady(true)}
          onChange={(event) => setPaymentType(event.collapsed ? undefined : event.value.type)}
        />
        {isConfirmed && <p className="mt-3">Stripe accepted this payment method. Try again to finish saving it.</p>}
        {stripeError && <p className="mt-3 text-normal text-error">{stripeError}</p>}
      </Form>
    );
  }

  return (
    <JobsTrialStep
      {...stepProps}
      paymentForm={paymentForm}
      startDisabled={!isReady || needsVerification || (!paymentSaved && !isElementReady)}
      isStarting={isStarting || isSaving}
      onStart={handleStart}
    />
  );
};
